import React, { useState, useEffect, useRef } from 'react';
import './UploadPage.css';
import { useNavigate } from 'react-router-dom';
import { uploadFile, uploadChunkedFile, checkAvailableSpace, getPendingUploads, clearPendingUpload, resumeUpload } from '../../services/fileTransferService';
import { validateCsvFile } from '../../services/csvValidationService';
import { sessionManager } from '../../utils/sessionManager.js';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import appInsightsLogger from '../../utils/appInsights.js';
import { CONFIG } from '../../config/config.js';
import { validateSecureFilename, sanitizeHtml, sanitizeErrors } from '../../utils/sanitize.js';

const LARGE_FILE_THRESHOLD = CONFIG.LARGE_FILE_THRESHOLD;

function UploadPage({ organizationId, userId }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [displayFileName, setDisplayFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [lastFileName, setLastFileName] = useState('');
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileSizeErrorMsg, setFileSizeErrorMsg] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [isClientSideValidationFailure, setIsClientSideValidationFailure] = useState(false);

  const [uploadProgress, setUploadProgress] = useState({
    progress: 0,
    phase: '',
    uploadSpeed: 0,
    estimatedTimeRemaining: 0,
    currentChunk: 0,
    totalChunks: 0
  });
  const [pendingUploads, setPendingUploads] = useState([]);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const navigate = useNavigate();
  const messageRef = useRef(null);

  // Helper function to handle secure file selection
  const handleSecureFileSelection = (file) => {
    if (!file) return;

    // Always update the display filename to show what the user selected
    setDisplayFileName(file.name);

    // Reset previous states
    setUploadSuccess(false);
    setUploadFailed(false);
    setFileSizeError(false);
    setFileSizeErrorMsg('');

    // Check file extension first
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv')) {
      appInsightsLogger.trackEvent('File_Upload_Validation_Failed', {
        component: 'UploadPage',
        fileName: file.name,
        reason: 'invalid_file_type'
      });

      setUploadSuccess(false);
      setUploadFailed(true);
      setLastFileName(file.name);
      setErrorMsg(`Invalid file format. Only CSV files accepted.`);
      setSelectedFile(null); // Clear selected file on error
      setDisplayFileName('');
      setFileInputKey(Date.now());
      return;
    }

    // Check file size
    if (file.size > CONFIG.MAX_FILE_SIZE) {
      const fileSizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
      setFileSizeError(true);
      setFileSizeErrorMsg(`File size (${fileSizeGB}GB) exceeds the maximum allowed size of ${CONFIG.MAX_FILE_SIZE_GB}GB. Please select a smaller file.`);
      setSelectedFile(null); // Clear selected file on error
      return;
    }

    // Perform filename security validation
    const validationResult = validateSecureFilename(file.name);

    if (!validationResult.isValid) {
      appInsightsLogger.trackEvent('File_Upload_Security_Rejected', {
        component: 'UploadPage',
        originalFilename: file.name,
        reason: validationResult.errorMessage,
        securityThreats: validationResult.securityThreats
      });

      setUploadFailed(true);
      setErrorMsg(validationResult.errorMessage);
      setLastFileName(file.name);
      setSelectedFile(null); // Clear selected file on validation failure
      return;
    }

    // Log successful validation
    appInsightsLogger.trackEvent('File_Upload_Security_Validated', {
      component: 'UploadPage',
      filename: file.name
    });

    // File is valid, proceed with upload
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || isUploading) {
      return;
    }

    // Clear all previous success/error messages before starting new upload
    setUploadSuccess(false);
    setUploadFailed(false);
    setErrorMsg('');
    setFileSizeError(false);
    setFileSizeErrorMsg('');
    setValidationErrors([]);
    setValidationWarnings([]);
    setIsClientSideValidationFailure(false);
    const fileSizeMB = selectedFile.size / (1024 * 1024);

    setLastFileName(selectedFile.name);
    setIsUploading(true);
    setUploadProgress({
      progress: 0,
      phase: 'uploading',
      uploadSpeed: 0,
      estimatedTimeRemaining: 0,
      currentChunk: 0,
      totalChunks: 0
    });

    // Client-side CSV validation before uploading
    const csvValidation = validateCsvFile(selectedFile);
    if (!csvValidation.isValid) {
      appInsightsLogger.trackEvent('File_Upload_ClientValidation_Failed', {
        component: 'UploadPage',
        fileName: selectedFile.name,
        reason: csvValidation.error
      });
      setUploadFailed(true);
      setErrorMsg(csvValidation.error);
      setIsUploading(false);
      setUploadProgress({ progress: 0, phase: '', uploadSpeed: 0, estimatedTimeRemaining: 0, currentChunk: 0, totalChunks: 0 });
      setSelectedFile(null);
      setDisplayFileName('');
      setFileInputKey(Date.now());
      return;
    }

    // Mark activity at upload start
    sessionManager.updateActivity();

    try {
      const spaceCheck = await checkAvailableSpace(selectedFile.size);
      if (!spaceCheck.hasSpace) {
        appInsightsLogger.trackEvent('File_Upload_Space_Check_Failed', {
          component: 'UploadPage',
          fileName: selectedFile.name,
          fileSizeMB: fileSizeMB.toFixed(2),
          availableSpaceMB: (spaceCheck.availableSpace / (1024 * 1024)).toFixed(2)
        });

        setUploadSuccess(false);
        setUploadFailed(true);
        setErrorMsg(spaceCheck.message || 'There is not enough space available for this file upload.');
        setIsUploading(false);
        setUploadProgress({
          progress: 0,
          phase: '',
          uploadSpeed: 0,
          estimatedTimeRemaining: 0,
          currentChunk: 0,
          totalChunks: 0
        });
        setSelectedFile(null);
        setDisplayFileName('');
        setFileInputKey(Date.now());
        return;
      }
    } catch (spaceError) {
      console.warn('Space check failed, continuing with upload:', spaceError);
    }

    appInsightsLogger.trackEvent('File_Upload_Started', {
      component: 'UploadPage',
      fileName: selectedFile.name,
      fileSizeMB: fileSizeMB.toFixed(2),
      isLargeFile: selectedFile.size > LARGE_FILE_THRESHOLD
    });

    try {
      let result;

      if (selectedFile.size > LARGE_FILE_THRESHOLD) {
        // Use chunked upload for large files
        result = await uploadChunkedFile(
          {
            file: selectedFile,
            organizationId,
            userId,
          },
          (progressData) => {
            setUploadProgress(progressData);
            // Mark activity during chunk upload progress
            sessionManager.updateActivity();
          }
        );
      } else {
        // Use regular upload for small files
        result = await uploadFile(
          {
            file: selectedFile,
            organizationId,
            userId,
          }
        );
        // Mark activity after direct upload
        sessionManager.updateActivity();
      }

      if (result && (result.success || result.status === 'Success')) {
        appInsightsLogger.trackEvent('File_Upload_Success', {
          component: 'UploadPage',
          fileName: selectedFile.name,
          uploadMethod: selectedFile.size > LARGE_FILE_THRESHOLD ? 'chunked' : 'direct'
        });

        setUploadSuccess(true);
        setUploadFailed(false);
      } else {
        appInsightsLogger.trackEvent('File_Upload_Failed', {
          component: 'UploadPage',
          fileName: selectedFile.name,
          reason: 'server_response_failure'
        });

        setUploadSuccess(false);
        setUploadFailed(true);
        setErrorMsg(result.message || `File "${selectedFile.name}" upload failed.`);
      }
    } catch (error) {
      if (error.isValidationError) {
        // Chunked upload was stopped by server-side validation failure
        setValidationErrors(error.validationErrors || []);
        setValidationWarnings(error.validationWarnings || []);
        setIsClientSideValidationFailure(false);
        appInsightsLogger.trackEvent('CSV_Chunk_Validation_Failed', {
          component: 'UploadPage',
          fileName: selectedFile.name,
          chunkIndex: error.chunkIndex,
          invalidRows: error.invalidRows,
          errorCount: error.validationErrors?.length || 0
        });
        setUploadSuccess(false);
        setUploadFailed(true);
        setErrorMsg(error.message || 'CSV validation failed. Please correct your file and try again.');
      } else {
        appInsightsLogger.trackException(error, {
          component: 'UploadPage',
          fileName: selectedFile.name
        });
        setUploadSuccess(false);
        setUploadFailed(true);
        setErrorMsg(error.message || `File "${selectedFile.name}" upload failed. Please try again.`);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress({
        progress: 0,
        phase: '',
        uploadSpeed: 0,
        estimatedTimeRemaining: 0,
        currentChunk: 0,
        totalChunks: 0
      });
    }

    setSelectedFile(null);
    setDisplayFileName('');
    setFileInputKey(Date.now());
  };

  useEffect(() => {
    const pending = getPendingUploads();
    if (pending.length > 0) {
      setPendingUploads(pending);
      setShowResumeDialog(true);
    }
  }, []);

  useEffect(() => {
    if (uploadSuccess || uploadFailed) {
      if (messageRef.current) {
        messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [uploadSuccess, uploadFailed]);

  // Generate CSV content from validation errors
  const generateValidationErrorsCsv = () => {
    const csvHeader = 'Error Number,Row Number,Field Name,Error Description,Timestamp\n';
    const csvRows = validationErrors.map((error, index) => {
      const escapeCsv = (str) => (str.includes(',') || str.includes('"') || str.includes('\n'))
        ? `"${str.replace(/"/g, '""')}"`
        : str;

      // Handle column/header-level errors: "Column N: Expected header *X*, found *Y*"
      const colMatch = error.match(/^column\s+\d+:\s*/i);
      if (colMatch) {
        const fieldMatch = error.match(/Expected header \*(.+?)\*/i);
        const fieldName = fieldMatch ? fieldMatch[1].trim() : '';
        const description = error.slice(colMatch[0].length); // strip "Column N: " prefix
        return `${index + 1},Header,${escapeCsv(fieldName)},${escapeCsv(description)},${new Date().toISOString()}`;
      }

      // Handle row-level errors: "Row N: ..."
      const rowMatch = error.match(/^Row (\d+):\s*(.+)$/);
      let rowNumber = '';
      let errorDescription = error;
      let fieldName = '';
      if (rowMatch) {
        rowNumber = rowMatch[1];
        errorDescription = rowMatch[2];
        const fieldMatch = errorDescription.match(/^(.+?)(?:\s+(?:is required|must contain|must be|cannot be|should be|does not|contains|exceeds|has|must))/i);
        if (fieldMatch) fieldName = fieldMatch[1].trim();
      }
      return `${index + 1},${rowNumber},${escapeCsv(fieldName)},${escapeCsv(errorDescription)},${new Date().toISOString()}`;
    }).join('\n');
    return csvHeader + csvRows;
  };

  const downloadValidationErrorsCsv = () => {
    if (validationErrors.length === 0) return;
    const blob = new Blob([generateValidationErrorsCsv()], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const baseFileName = (selectedFile?.name || lastFileName || 'file').replace(/\.csv$/i, '');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    link.setAttribute('href', url);
    link.setAttribute('download', `${baseFileName}_validation_errors_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Clear all previous success/error messages when starting to select a new file
  const clearMessages = () => {
    setUploadSuccess(false);
    setUploadFailed(false);
    setErrorMsg('');
    setFileSizeError(false);
    setFileSizeErrorMsg('');
    setValidationErrors([]);
    setValidationWarnings([]);
    setIsClientSideValidationFailure(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleSecureFileSelection(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleSecureFileSelection(file);
    }
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    setDisplayFileName('');
    setFileInputKey(Date.now());
    setUploadSuccess(false);
    setUploadFailed(false);
    setFileSizeError(false);
    setFileSizeErrorMsg('');
  };

  const handleResumeUpload = async (uploadData) => {
    setShowResumeDialog(false);
    setIsUploading(true);
    setUploadProgress({
      progress: 0,
      phase: 'uploading',
      uploadSpeed: 0,
      estimatedTimeRemaining: 0,
      currentChunk: 0,
      totalChunks: 0
    });

    try {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.csv';
      fileInput.onchange = async (e) => {
        const file = e.target.files[0];
        if (file && file.name === uploadData.fileName) {
          const result = await resumeUpload(
            uploadData.uploadId,
            file,
            uploadData.organizationId,
            uploadData.userId,
            (progressData) => {
              setUploadProgress(progressData);
            }
          );

          if (result && result.success) {
            setUploadSuccess(true);
            setUploadFailed(false);
            setLastFileName(file.name);
          } else {
            setUploadFailed(true);
            setErrorMsg('Failed to resume upload');
          }
        } else {
          setUploadFailed(true);
          setErrorMsg('Please select the same file that was being uploaded');
        }
        setIsUploading(false);
      };
      fileInput.click();
    } catch (error) {
      console.error('Resume upload failed:', error);
      setUploadFailed(true);
      setErrorMsg('Failed to resume upload');
      setIsUploading(false);
    }
  };

  const handleClearPendingUpload = (uploadId) => {
    clearPendingUpload(uploadId);
    setPendingUploads(pending => pending.filter(p => p.uploadId !== uploadId));
    if (pendingUploads.length <= 1) {
      setShowResumeDialog(false);
    }
  };

  return (
    <>
      {isUploading && (
        <LoadingSpinner overlay size="large" />
      )}

      {showResumeDialog && pendingUploads.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Resume Interrupted Uploads?</h3>
            <p style={{ marginBottom: '20px' }}>
              We detected {pendingUploads.length} interrupted upload{pendingUploads.length > 1 ? 's' : ''}. Would you like to resume?
            </p>
            {pendingUploads.map(upload => (
              <div key={upload.uploadId} style={{
                border: '1px solid #ddd',
                padding: '15px',
                marginBottom: '10px',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{upload.fileName}</div>
                  <div style={{ fontSize: '0.9em', color: '#666' }}>
                    Progress: {upload.uploadedChunks.length} / {upload.totalChunks} chunks uploaded
                    ({((upload.uploadedChunks.length / upload.totalChunks) * 100).toFixed(1)}%)
                  </div>
                  <div style={{ fontSize: '0.85em', color: '#999', marginTop: '3px' }}>
                    {(upload.fileSize / (1024 * 1024)).toFixed(2)} MB
                  </div>
                </div>
                <div>
                  <button
                    onClick={() => handleResumeUpload(upload)}
                    style={{
                      backgroundColor: '#009688',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '8px'
                    }}
                  >
                    Resume
                  </button>
                  <button
                    onClick={() => handleClearPendingUpload(upload.uploadId)}
                    style={{
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            ))}
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowResumeDialog(false)}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="upload-main">
        <h2 className="upload-title">Upload Member Eligibility Data</h2>
        <div className="upload-card">
          <div className="upload-section-title">Select File to Upload (Maximum 5 GB Allowed)</div>
          <div
            className={`upload-dropzone${dragActive ? ' drag-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="upload-folder-icon">📁</div>
            <div>Drag and drop your CSV file here</div>
            <div className="upload-or">or</div>
            <label className="upload-browse-btn" onClick={clearMessages}>
              <input
                key={fileInputKey}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={isUploading}
              />
              <span style={{
                opacity: isUploading ? 0.6 : 1,
                pointerEvents: isUploading ? 'none' : 'auto',
                cursor: isUploading ? 'not-allowed' : 'pointer'
              }}>Browse Files</span>
            </label>
            <div className="upload-selected-file">
              {displayFileName ? (
                <>
                  <span>{displayFileName}</span>{' '}
                  <span className="delete-file" onClick={handleDeleteFile} title="Remove file">❌</span>
                </>
              ) : (
                'Selected file will appear here'
              )}
            </div>
          </div>
        </div>

        {isUploading && uploadProgress.progress >= 0 && (
          <div className="upload-progress" style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                  {uploadProgress.phase === 'uploading' ? 'Uploading...' :
                   uploadProgress.phase === 'completed' ? 'Upload Complete!' :
                   uploadProgress.phase === 'error' ? 'Upload Stopped' : 'Processing...'}
                </span>
                <span style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#009688' }}>
                  {uploadProgress.progress.toFixed(1)}%
                </span>
              </div>
              {uploadProgress.totalChunks > 1 && (
                <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '4px' }}>
                  Chunk {uploadProgress.currentChunk} of {uploadProgress.totalChunks}
                </div>
              )}
              {uploadProgress.uploadSpeed > 0 && (
                <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '4px' }}>
                  Upload Speed: {uploadProgress.uploadSpeed.toFixed(2)} MB/s
                </div>
              )}
              {uploadProgress.estimatedTimeRemaining > 0 && (
                <div style={{ fontSize: '0.9em', color: '#666' }}>
                  Time Remaining: {Math.floor(uploadProgress.estimatedTimeRemaining / 60)}m {uploadProgress.estimatedTimeRemaining % 60}s
                </div>
              )}
            </div>
            <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '4px', height: '24px', overflow: 'hidden' }}>
              <div style={{
                width: `${uploadProgress.progress}%`,
                backgroundColor: uploadProgress.phase === 'completed' ? '#4caf50' : '#009688',
                height: '100%',
                borderRadius: '4px',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.85em',
                fontWeight: 'bold'
              }}>
                {uploadProgress.progress > 10 && `${uploadProgress.progress.toFixed(0)}%`}
              </div>
            </div>
          </div>
        )}

        <div className="upload-actions">
          <button
            className="upload-btn"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading || fileSizeError}
            style={{
              opacity: (!selectedFile || isUploading || fileSizeError) ? 0.6 : 1,
              cursor: (!selectedFile || isUploading || fileSizeError) ? 'not-allowed' : 'pointer'
            }}
          >
            Upload File
          </button>
          <button
            className="cancel-btn"
            onClick={() => navigate('/dashboard')}
            disabled={isUploading}
          >
            Cancel
          </button>
        </div>
        <br />
        <div ref={messageRef}>
          {fileSizeError && (
            <div className="upload-error" style={{ marginBottom: '10px' }}>
              <span className="error-cross">✗</span>
              <span className="error-title">File Size Error</span>
              <div>{fileSizeErrorMsg}</div>
            </div>
          )}
          {uploadSuccess && (
            <div className="upload-success">
              <span className="success-check">✓</span>
              <span className="success-title">Upload Successful</span>
              <div>
                File &quot;{lastFileName}&quot; has been uploaded successfully.
              </div>
            </div>
          )}
          {uploadFailed && (
            <div className="upload-error">
              <span className="error-cross">✗</span>
              <span className="error-title">Upload Failed</span>
              <div>{errorMsg}</div>
              {validationErrors.length > 0 && isClientSideValidationFailure && (
                <ul style={{ marginTop: '10px', marginBottom: '4px', paddingLeft: '20px', fontSize: '0.92rem', color: '#721c24' }}>
                  {validationErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
              {validationErrors.length > 0 && !isClientSideValidationFailure && (
                <div style={{ marginTop: '12px' }}>
                  <button
                    onClick={downloadValidationErrorsCsv}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#009688',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#00796B'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#009688'}
                  >
                    📥 Download Error Report ({validationErrors.length} errors)
                  </button>
                </div>
              )}
              {validationWarnings.length > 0 && (
                <div style={{ fontSize: '0.9rem', color: '#856404', marginTop: '8px' }}>
                  ⚠ {validationWarnings.length} warning(s) detected in the file.
                </div>
              )}
              <div style={{ fontSize: '0.95rem', marginTop: '6px' }}>
                Please review and correct the file, then try again.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default UploadPage;
